import React from "react";
import {
  Phone, MessageSquare, Calendar, Activity, Brain, Sparkles,
  Headphones, Mail, Stethoscope, Users,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  AgentCard, ParallelGrid, ConversationThread, FeatureCallout,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogStephanieHygieneRecallConversations = () => (
  <RichBlogLayout
    category="Patient Reactivation"
    eyebrow="Hygiene Recall"
    title="Stephanie: Hundreds of Hygiene Recall Conversations, All at Once"
    subtitle="Continuous, two-way recall outreach across SMS and voice — adapting to each patient instead of blasting them."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's 11:08 AM and Lara, the office manager, opens the recall report.
        532 active hygiene patients are past due — 184 of them by more than a
        year. One name on screen got a text last March. Another got a voicemail
        in July that nobody circled back on. Most got nothing at all, because
        the last three weeks at the front desk have been phones, check-ins,
        and chart pulls back-to-back.
      </p>
      <p>
        These patients didn't decide to leave. They drifted, because the only
        thing that brings them back — patient, persistent outreach across weeks
        — is exactly the thing a busy front desk cannot realistically do.
      </p>
    </Prose>

    <AgentCard
      name="Stephanie"
      role="AI Hygiene Recall · SMS + Voice"
      initial="S"
      description="Stephanie runs real, ongoing recall conversations with overdue hygiene patients across SMS and voice — hundreds of threads at once. Each one adapts to who replies, who doesn't, and who asked her to try again next month."
      channels={["SMS", "Outbound voice", "Multi-week cadence", "Adaptive"]}
    />

    <Prose>
      <h2>What an honest recall list looks like</h2>
      <p>
        A typical practice carries 400–800 patients overdue for hygiene on the
        list this morning. To work that list well, you'd need to call each one,
        track who said what, follow up at the right interval, switch to text
        when calls don't connect, and remember the one who asked you to try
        again after Memorial Day. Across hundreds of patients, over weeks. It's
        a full-time job nobody has the time to do.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, prefix: "", suffix: "+", label: "Concurrent recall conversations" },
        { value: 0, label: "Generic blast messages" },
        { value: 12, suffix: "+ mo", label: "Reactivates patients who lapsed long ago" },
        { value: 2, label: "Channels working together: SMS + voice" },
      ]}
    />

    <Prose>
      <p>
        Stephanie isn't a reminder blast with a friendly tone. She's running
        hundreds of independent, two-way conversations concurrently — each
        one shaped by what that patient actually said, when they last came in,
        and how they typically prefer to be contacted.
      </p>

      <h2>Recall blasts vs. recall conversations</h2>
      <p>
        Most recall systems are mass-message engines. They take your overdue
        list, pick a template, fire it off, and call it done. The response
        rate is whatever it is. The patients who don't reply just sit there.
        The ones who do reply often get a second template back — or worse,
        nothing, because no one is watching the inbox.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Recall blasts"
      afterTitle="Stephanie · ongoing recall conversations"
      beforeItems={[
        "One generic message goes to everyone on the overdue list",
        "Replies sit in an inbox no one is monitoring",
        "No follow-up cadence — one shot and done",
        "Same message regardless of how long the patient has been gone",
      ]}
      afterItems={[
        "Each patient gets a tailored opening based on history and behavior",
        "Stephanie reads and responds to replies in real time",
        "Multi-week cadence: SMS, voice, voicemail, retry — adapts as it goes",
        "12-month-lapsed patient gets a different approach than a 90-day-overdue one",
      ]}
    />

    <Prose>
      <h2>A live snapshot at 2:14 PM on a Wednesday</h2>
      <p>
        Drop in on Stephanie mid-shift and dozens of patient threads are open
        at the same time. Some are texting back about scheduling. Some are
        asking insurance questions. Some are getting a follow-up voice call
        because they didn't respond to the last two texts. None of these
        threads are interfering with each other, and none of them are dropping
        because the front desk got pulled into a checkout.
      </p>
    </Prose>

    <ParallelGrid
      title="Right now · live recall threads"
      columns={3}
      items={[
        { title: "Maria L.", subtitle: "9 mo overdue · SMS · waiting reply", icon: MessageSquare, status: "active" },
        { title: "James P.", subtitle: "14 mo · Voice · scheduling", icon: Phone, status: "active" },
        { title: "Anna R.", subtitle: "6 mo · SMS · booked Friday 10am", icon: Calendar, status: "done" },
        { title: "David K.", subtitle: "11 mo · asked to retry in May", icon: Activity, status: "pending" },
        { title: "Priya S.", subtitle: "8 mo · SMS · insurance question", icon: MessageSquare, status: "active" },
        { title: "Linda M.", subtitle: "16 mo · Voicemail left", icon: Headphones, status: "active" },
        { title: "Tom B.", subtitle: "7 mo · SMS · booked Tuesday 2pm", icon: Calendar, status: "done" },
        { title: "Sofia G.", subtitle: "13 mo · Voice attempt 3 of 3", icon: Phone, status: "active" },
        { title: "Carlos V.", subtitle: "10 mo · SMS · two-way thread", icon: MessageSquare, status: "active" },
      ]}
    />

    <Prose>
      <h2>How she decides what to say</h2>
      <p>
        Stephanie doesn't pick a template. For each patient, she's reasoning
        through context: what was their last visit type, what's in the clinical
        notes, how have they responded to outreach in the past, do they prefer
        morning or evening texts, did they mention financial concerns last
        time. Based on that, she chooses the channel, the tone, and the timing.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Identifies overdue patients continuously",
          body:
            "Stephanie watches the recall list as it changes. The moment a patient goes past their hygiene interval, she's in the queue.",
        },
        {
          title: "Reads the patient's full context",
          body:
            "Communication history, last appointment notes, prior responses, financial flags, clinical relevance — all pulled before the first message goes out.",
        },
        {
          title: "Picks the right channel and tone",
          body:
            "Some patients respond to texts. Some need a voice call. Stephanie chooses based on past behavior, not a default.",
        },
        {
          title: "Runs a two-way conversation",
          body:
            "When the patient replies, Stephanie responds in context — answering questions, checking the schedule, confirming insurance.",
        },
        {
          title: "Adapts the cadence over weeks",
          body:
            "If a patient asks her to follow up later, she remembers. If a text bounces, she pivots to a call. If a voicemail goes unanswered, she varies the message and tries a different time of day.",
        },
        {
          title: "Books the appointment in the PMS",
          body:
            "When the patient is ready, Stephanie books the slot directly into the schedule — correct provider, correct operatory, correct duration.",
        },
      ]}
    />

    <PullQuote>
      The 14-month-lapsed patient gets a different opening, a different cadence,
      and a different tone than the 90-day-overdue one. That's the shift.
    </PullQuote>

    <Prose>
      <h2>One thread, over four weeks</h2>
      <p>
        Take Maria, a hygiene patient who's been overdue for 9 months. She
        ignored a generic recall text last spring. Here's what the conversation
        looks like when Stephanie picks it up.
      </p>
    </Prose>

    <ConversationThread
      title="Stephanie · recall thread · Maria L."
      channel="SMS"
      messages={[
        { from: "system", text: "Day 1 · initial outreach" },
        { from: "agent", text: "Hi Maria, this is Stephanie at Bayshore Dental. We noticed you're overdue for your hygiene visit — last cleaning was July. Want me to find a time that works?" },
        { from: "patient", text: "Hey, yeah, things have been busy. Maybe in a few weeks?" },
        { from: "agent", text: "Totally — I'll check back in a couple of weeks. If anything changes before then, just text me and I'll find a slot." },
        { from: "system", text: "Day 16 · follow-up" },
        { from: "agent", text: "Hi Maria — circling back. We've got openings the next two weeks, including a couple of evening slots. Want me to send a few options?" },
        { from: "patient", text: "Sure, evenings would be better." },
        { from: "agent", text: "Got it. Tuesday 5:30 PM, Thursday 6:00 PM, or next Wednesday at 5:45 PM — which works?" },
        { from: "patient", text: "Thursday at 6 works." },
        { from: "system", text: "Booked · Thursday 6:00 PM with Lara · confirmation sent" },
      ]}
    />

    <Prose>
      <h2>The patient who hasn't been in for a year</h2>
      <p>
        The patients you're really losing aren't the 90-day overdue ones —
        they're the ones who lapsed 12 or 14 or 18 months ago. In most
        practices, those names just sit in the system as "lost." A recall
        blast won't bring them back. A six-week persistent, personal,
        adaptive conversation might.
      </p>
      <p>
        Stephanie is built for those patients. She approaches them differently.
        The opening is warmer, less transactional. The cadence is patient. The
        tone matches the relationship. And critically — she keeps trying, in
        different ways, without ever crossing into spam.
      </p>
    </Prose>

    <FeatureCallout
      stat="Hundreds"
      label="Of recall conversations, simultaneously"
      description="Stephanie runs hundreds of personalized recall threads at once, across SMS and voice, adapting to every reply — without adding work to the front desk."
      icon={Sparkles}
    />

    <Prose>
      <h2>What's left for the front desk</h2>
      <p>
        With Stephanie running recall off the front desk, the team stops being
        a recall unit. They're not chasing lists anymore. They're not manually
        tracking who responded to what. The recall workflow disappears from
        their day.
      </p>
      <p>
        What they see instead is a steady drip of confirmed bookings landing
        in the schedule, plus a small handful of "warm handoffs" — patients
        who are actively engaging and ready to talk. Those are the
        conversations a human should have. The persistent, weeks-long nurturing
        underneath is already taken care of.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        The recall list is no longer something your team works through. It's
        something Stephanie is always working through. Hundreds of patients,
        each on their own thread, each getting outreach that actually fits them
        — quietly turning lapsed names into booked hygiene visits.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogStephanieHygieneRecallConversations;
